import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { addDays, eachDayOfInterval, format } from 'date-fns';
import { useMemo, useState } from 'react';

import { cn } from '~/styles/extended-theme';
import { formatInterval } from '~/utils/format-interval';

const DAYS_TO_SHOW = 7;
const getDayOfWeek = (date: Date) => format(date, 'EEE');
const getDayOfMonth = (date: Date) => format(date, 'd');

const useClassDateSelect = (dateStartInitial: Date) => {
  const [dateStart, setDateStart] = useState(dateStartInitial);

  const dates = useMemo(() => {
    return eachDayOfInterval({
      start: dateStart,
      end: addDays(dateStart, DAYS_TO_SHOW - 1),
    });
  }, [dateStart]);

  const intervalDisplay = useMemo(() => {
    const start = dates[0];
    const end = dates[dates.length - 1];
    return formatInterval(start, end);
  }, [dates]);

  const handleNext = () => {
    setDateStart((date) => addDays(date, DAYS_TO_SHOW));
  };

  const handlePrevious = () => {
    setDateStart((date) => addDays(date, -DAYS_TO_SHOW));
  };

  return {
    dates,
    handleNext,
    handlePrevious,
    intervalDisplay,
  };
};

// keeping the start and selected dates separately so that the displayed dates don't jump around when the selection changes
interface ClassDateSelectProps {
  dateStart: Date;
  selectedDate: Date | null;
  onSelectedDateChanged: (date: Date) => void;
}

export default function ClassDateSelect({
  dateStart,
  selectedDate,
  onSelectedDateChanged,
}: ClassDateSelectProps) {
  const { dates, handleNext, handlePrevious, intervalDisplay } = useClassDateSelect(dateStart);

  const handleSelect = (date: Date) => {
    onSelectedDateChanged(date);
  };

  const isSelected = (date: Date) =>
    date.getFullYear() === selectedDate?.getFullYear() &&
    date.getMonth() === selectedDate?.getMonth() &&
    date.getDate() === selectedDate?.getDate();

  return (
    <div>
      <p className="mb-2 text-center text-subtitle2 font-normal">{intervalDisplay}</p>

      <div className="flex justify-between gap-2">
        <button type="button" onClick={handlePrevious}>
          <ChevronLeftIcon />
        </button>

        {dates.map((date) => (
          <button
            type="button"
            key={date.toISOString()}
            onClick={() => handleSelect(date)}
            className={cn('rounded-lg p-2 text-center', isSelected(date) && 'bg-gray-200')}
          >
            <p className="text-body2 uppercase">{getDayOfWeek(date)}</p>
            <p className="text-xl font-light">{getDayOfMonth(date)}</p>
          </button>
        ))}

        <button type="button" onClick={handleNext}>
          <ChevronRightIcon />
        </button>
      </div>
    </div>
  );
}
